import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { ManagementIsfModel } from '@data-layer/models/management';
import { PaginatedResultModel, ShortModel } from '@data-layer/models/common';
import { IsfCountByStatusModel, IsfModel } from '@data-layer/models/isf';
import { map } from 'rxjs/operators';
import { enumIsfState, WizardProgressEnum } from '@data-layer/enums/isf';
import { enumOcrStatus, enumPlatformType } from '@data-layer/enums/common';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { enumMarineInsuranceStatusType } from '../enums/marine-insurance/enumInsuranceStatusType.enum';

@Injectable({
  providedIn: 'root',
})
export class IsfRepository {
  constructor(private apiService: ApiService) {}

  public getIsfList(companyId: number, offset: number, limit: number, filters?: any): Observable<PaginatedResultModel<IsfModel>> {
    return this.apiService.get(`${environment.url.prefixCustoms}v2/companies/${companyId}/isfs`, { params: { offset, limit, ...filters } });
  }

  public create(companyId: number, isf: Partial<IsfModel>): Observable<IsfModel> {
    return this.apiService.post(`${environment.url.prefixCustoms}v2/companies/${companyId}/isfs`, isf);
  }

  public getApprovedIsfList(companyId: number): Observable<IsfModel[]> {
    return this.apiService.get(`${environment.url.prefixCustoms}v2/companies/${companyId}/isfs/mergeable`);
  }

  public send(id: number): Observable<IsfModel> {
    return this.apiService.post(`${environment.url.isfs}sent`, null, {
      params: { id },
    });
  }

  public duplicate(isf: IsfModel | ManagementIsfModel): Observable<IsfModel> {
    return this.apiService.put(`${environment.url.isfs}duplicate/${isf.id}`);
  }

  public update(isf: Partial<IsfModel>, isShowLoader: boolean = true): Observable<IsfModel> {
    return this.apiService.put(
      `${environment.url.isfs}${isf.id}`,
      {
        ...isf,
      },
      null,
      { isShowLoader },
    );
  }

  public updatePlatform(isfId: number, platformType: enumPlatformType): Observable<IsfModel> {
    return this.apiService.put(`${environment.url.isfs}${isfId}/platformType`, platformType, null, { isShowLoader: false });
  }

  public updateInternalNote(isfId: number, data: InternalNote): Observable<void> {
    return this.apiService.put(`${environment.url.isfs}${isfId}/internal-note`, data);
  }

  public updateInsuranceStatus(isfId: number, status: enumMarineInsuranceStatusType): Observable<never> {
    return this.apiService.post(`${environment.url.isfsInsurance}${isfId}/marine-insurance/status`, status);
  }

  public delete(isfId: number): Observable<never> {
    return this.apiService.delete(`${environment.url.isfs}${isfId}`);
  }

  public getSummary(referenceNumber: string): Observable<IsfModel> {
    return this.apiService
      .get<IsfModel>(`${environment.url.isfs}${referenceNumber}`)
      .pipe(map((isf) => ({ ...isf, importerId: isf?.importer?.id })));
  }

  public getConnectedISFs(esId: number): Observable<ShortModel[]> {
    return this.apiService.get<ShortModel[]>(`${environment.url.prefixCustoms}v2/entry-summaries/${esId}/connected-isfs`);
  }

  public updateWizardStep(id: number, step: WizardProgressEnum): Observable<IsfModel> {
    return this.apiService.put(`${environment.url.isfs}${id}/wizard-progress`, `"${step}"`, null, { isShowLoader: false });
  }

  public getIsfCountStats(companyId: number): Observable<IsfCountByStatusModel[]> {
    return this.apiService.get<IsfCountByStatusModel[]>(`${environment.url.prefixCustoms}v2/companies/${companyId}/isfs-count-by-status`);
  }

  public getState(id: number): Observable<enumIsfState> {
    return this.apiService.get(`${environment.url.prefixCustoms}v2/isfs/${id}/state`, null, { isShowLoader: false });
  }

  public getOcrStatus(isfId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.apiService.get(`${environment.url.prefixCustoms}v2/isfs/${isfId}/ocr-status`, null, { isShowLoader });
  }

  public resetOcrStatus(isfId: number): Observable<enumOcrStatus> {
    return this.apiService.delete(`${environment.url.prefixCustoms}v2/isfs/${isfId}/ocr`);
  }

  public ocrProcessing(isfId: number): Observable<void> {
    return this.apiService.post<void>(`${environment.url.prefixCustoms}v2/isfs/${isfId}/ocr`);
  }
}
